import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { parseEditorJsonToText, parseTextToEditorContent } from '@/lib/tiptap'
import { cn } from '@/lib/utils'
import type { ServerStatus, UploadFile } from '@/services/media-upload.service'
import { PrimitiveAtom, useAtom } from 'jotai'
import Document from '@tiptap/extension-document'
import { HardBreak } from '@tiptap/extension-hard-break'
import History from '@tiptap/extension-history'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import Text from '@tiptap/extension-text'
import { TextSelection } from '@tiptap/pm/state'
import { EditorContent, useEditor } from '@tiptap/react'
import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { ClipboardAndDropHandler } from './ClipboardAndDropHandler'
import TTEmojiX from './TTEmojiX'
import emojiSuggestion from './TTEmojiX/suggestion'
import Mention from './Mention'
import mentionSuggestion from './Mention/suggestion'
import TTSlash, { TComposerContext } from './TTSlash'
import { setEditorContext } from './TTSlash/composerContext'
import Preview from './Preview'
import { InlineHighlight } from './InlineHighlight'
import { Emoji } from '@nostr/gadgets/lists'
import { TComposerState } from '../composer-state'
import UploadPlaceholder from './UploadPlaceholder'

export type TPostTextareaHandle = {
  appendText: (text: string, addNewline?: boolean) => void
  insertText: (text: string) => void
  insertUploadPlaceholder: (file: UploadFile) => void
  replaceUploadPlaceholder: (file: UploadFile, url: string) => void
  insertEmoji: (emoji: string | Emoji) => void
}

const PostTextarea = forwardRef<
  TPostTextareaHandle,
  {
    text: string
    setText: Dispatch<SetStateAction<string>>
    defaultContent: string
    composerStateAtom: PrimitiveAtom<TComposerState>
    onSubmit?: () => void
    className?: string
    onUploadStart?: (file: UploadFile, cancel: () => void) => void
    onUploadEnd?: (file: UploadFile) => void
    onUploadInit?: (info: { file: UploadFile; cancel: () => void; servers: string[] }) => void
    onServerStatus?: (file: UploadFile, server: string, status: ServerStatus) => void
    /** target event info for slash command scope filtering and relay handling */
    slashComposerContext?: TComposerContext
  }
>(
  (
    {
      text = '',
      setText,
      defaultContent = '',
      composerStateAtom,
      onSubmit,
      className,
      onUploadStart,
      onUploadEnd,
      onUploadInit,
      onServerStatus,
      slashComposerContext
    },
    ref
  ) => {
    const { t } = useTranslation()
    const [tabValue, setTabValue] = useState('edit')
    const [composerState, setComposerState] = useAtom(composerStateAtom)
    const [initialContent] = useState(() =>
      composerState.content ? composerState.content : parseTextToEditorContent(defaultContent)
    )

    const editor = useEditor({
      autofocus: 'end',
      extensions: [
        Document,
        Paragraph,
        Text,
        History,
        HardBreak,
        Placeholder.configure({
          placeholder:
            t('Write something...') + ' (' + t('Paste or drop media files to upload') + ')'
        }),
        TTEmojiX.configure({
          suggestion: emojiSuggestion
        }),
        Mention.configure({
          suggestion: mentionSuggestion
        }),
        TTSlash.configure({}),
        InlineHighlight,
        UploadPlaceholder,
        ClipboardAndDropHandler.configure({
          onUploadStart: (file, cancel) => {
            onUploadStart?.(file, cancel)
          },
          onUploadEnd: (file) => onUploadEnd?.(file),
          onUploadInit: (info) => onUploadInit?.(info),
          onServerStatus: (file, server, status) => onServerStatus?.(file, server, status)
        })
      ],
      editorProps: {
        attributes: {
          class: cn(
            'border rounded-lg p-3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            className
          )
        },
        handleKeyDown: (_view, event) => {
          // Handle Ctrl+Enter or Cmd+Enter for submit
          if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
            event.preventDefault()
            onSubmit?.()
            return true
          }
          return false
        },
        clipboardTextSerializer(content) {
          return parseEditorJsonToText(content.toJSON())
        }
      },
      content: initialContent,
      onUpdate(props) {
        const contentJ = props.editor.getJSON()
        setText(parseEditorJsonToText(contentJ))
        setComposerState((curr) => ({ ...curr, content: contentJ }))
      },
      onCreate(props) {
        setText(parseEditorJsonToText(props.editor.getJSON()))
      }
    })

    useEffect(() => {
      setEditorContext(editor, slashComposerContext ?? null)
    }, [editor, slashComposerContext])

    useImperativeHandle(ref, () => ({
      appendText: (text: string, addNewline = false) => {
        if (editor) {
          let chain = editor
            .chain()
            .focus()
            .command(({ tr, dispatch }) => {
              if (dispatch) {
                const endPos = tr.doc.content.size
                const selection = TextSelection.create(tr.doc, endPos)
                tr.setSelection(selection)
                dispatch(tr)
              }
              return true
            })
            .insertContent(text)
          if (addNewline) {
            chain = chain.setHardBreak()
          }
          chain.run()
        }
      },
      insertText: (text: string) => {
        if (editor) {
          editor.chain().focus().insertContent(text).run()
        }
      },
      insertUploadPlaceholder: (file: UploadFile) => {
        if (!editor) return
        const node = editor.schema.nodes.uploadPlaceholder.create({
          uploadId: file._uploadId,
          fileName: file.name
        })
        editor
          .chain()
          .focus()
          .command(({ tr }) => {
            tr.replaceSelectionWith(node)
            tr.insert(tr.selection.from, editor.schema.nodes.hardBreak.create())
            return true
          })
          .run()
      },
      replaceUploadPlaceholder: (file: UploadFile, url: string) => {
        if (!editor) return
        const { state } = editor
        let replaced = false
        const tr = state.tr
        state.doc.descendants((node, pos) => {
          if (replaced || node.type.name !== 'uploadPlaceholder') return !replaced
          if (node.attrs.uploadId !== file._uploadId) return true
          tr.replaceWith(pos, pos + node.nodeSize, state.schema.text(url))
          replaced = true
          return false
        })
        if (replaced) editor.view.dispatch(tr)
      },
      insertEmoji: (emoji: string | Emoji) => {
        if (editor) {
          if (typeof emoji === 'string') {
            editor.chain().insertContent(emoji).run()
          } else {
            const emojiNode = editor.schema.nodes.emoji.create({
              name: emoji.shortcode
            })
            editor.chain().insertContent(emojiNode).insertContent(' ').run()
          }
        }
      }
    }))

    if (!editor) {
      return null
    }

    return (
      <Tabs
        defaultValue="edit"
        value={tabValue}
        onValueChange={(v) => setTabValue(v)}
        className="space-y-2"
      >
        <TabsList>
          <TabsTrigger value="edit">{t('Edit')}</TabsTrigger>
          <TabsTrigger value="preview">{t('Preview')}</TabsTrigger>
          <TabsTrigger value="raw">Raw</TabsTrigger>
        </TabsList>
        <TabsContent value="edit">
          <EditorContent className="tiptap" editor={editor} />
        </TabsContent>
        <TabsContent
          value="preview"
          onClick={() => {
            setTabValue('edit')
            editor.commands.focus()
          }}
        >
          <Preview content={text} className={className} />
        </TabsContent>
        <TabsContent value="raw">
          <pre className={cn('whitespace-pre-wrap break-words p-3', className)}>{text}</pre>
        </TabsContent>
      </Tabs>
    )
  }
)
PostTextarea.displayName = 'PostTextarea'
export default PostTextarea
